import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, Camera, X, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import './TradePage.css';
import SEO from '../SEO';
const API_URL = 'https://api.sohanihandicrafts.com:5010';

const TradePage = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gallery, setGallery] = useState(null); 
    const [photoIdx, setPhotoIdx] = useState(0);
    const [fullTextEvent, setFullTextEvent] = useState(null); // State for Description Modal

    useEffect(() => {
        const fetchEvents = async () => {
            const res = await axios.get(`${API_URL}/api/trade/active`);
            setEvents(res.data);
            setLoading(false);
        };
        fetchEvents();
    }, []);

    const openGallery = (imgs, index) => {
        setGallery(imgs);
        setPhotoIdx(index);
    };

    // Helper function to truncate text
    const truncateText = (text, limit) => {
        if (text.length <= limit) return text;
        return text.slice(0, limit) + "...";
    };

    if (loading) return <div className="trd-loader">Loading Heritage Fairs...</div>;

    return (
        <>
        <SEO 
  title="Global Trade Fairs & Exhibition Journey"
  description="View Sohani Handicrafts' presence at international trade fairs like IHGF Delhi Fair. Showcasing India's finest artisan furniture to the global market."
  keywords="ihgf delhi fair exhibitors, handicraft trade shows 2024, international furniture expo india, global trade fair presence, furniture exhibitions jodhpur"
  url="/trade"
/>
       
        <div className="trd-pg-root">
            <header className="trd-pg-header">
                <span className="trd-pg-tag">Global Presence</span>
                <h1>Trade Fairs & <span className="gold">Exhibitions</span></h1>
                <p>Witness Sohani Handicrafts showcasing the finest artisan woodcraft at major global platforms.</p>
            </header>

            <div className="trd-pg-container">
                {events.map((event, idx) => (
                    <motion.section 
                        key={event._id}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className={`trd-pg-event ${idx % 2 !== 0 ? 'reverse' : ''}`}
                    >
                        <div className="trd-pg-content">
                            <div className="trd-pg-meta">
                                <Calendar size={16}/> <span>{new Date(event.createdAt).getFullYear()}</span>
                                <MapPin size={16}/> <span>Global Fair</span>
                            </div>
                            <h2>{event.title}</h2>
                            
                            {/* TRUNCATED DESCRIPTION */}
                            <p className="trd-pg-desc">
                                {truncateText(event.description, 220)}
                                {event.description.length > 220 && (
                                    <button 
                                        className="trd-pg-more-btn" 
                                        onClick={() => setFullTextEvent(event)}
                                    >
                                        Read Full Story
                                    </button>
                                )}
                            </p>

                            <div className="trd-pg-stats">
                                <Camera size={18}/> <span>{event.images.length} High-Res Captures</span>
                            </div>
                        </div>

                        <div className="trd-pg-visuals">
                            <div className="trd-pg-grid">
                                {event.images.slice(0, 3).map((img, i) => (
                                    <div key={i} className={`trd-pg-img-box pos-${i}`} onClick={() => openGallery(event.images, i)}>
                                        <img src={`${API_URL}/${img}`} alt="event" />
                                        {i === 2 && event.images.length > 3 && (
                                            <div className="trd-pg-more-overlay">+{event.images.length - 3}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.section>
                ))}
            </div>

            {/* MODAL 1: FULL DESCRIPTION POPUP */}
            <AnimatePresence>
                {fullTextEvent && (
                    <motion.div 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        className="trd-text-modal-overlay"
                        onClick={() => setFullTextEvent(null)}
                    >
                        <motion.div 
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="trd-text-modal-card"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="trd-modal-close" onClick={() => setFullTextEvent(null)}><X/></button>
                            <span className="modal-tag">Detailed Exhibition Report</span>
                            <h2>{fullTextEvent.title}</h2>
                            <div className="modal-divider"></div>
                            <div className="modal-scroll-area">
                                <p>{fullTextEvent.description}</p>
                            </div>
                            <div className="modal-footer">
                                <BookOpen size={20} /> <span>Sohani Artisan Archives</span>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* MODAL 2: IMAGE LIGHTBOX */}
            <AnimatePresence>
                {gallery && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="trd-lightbox">
                        <button className="close-btn" onClick={() => setGallery(null)}><X size={40}/></button>
                        <button className="nav-btn left" onClick={() => setPhotoIdx((photoIdx - 1 + gallery.length) % gallery.length)}><ChevronLeft size={40}/></button>
                        <button className="nav-btn right" onClick={() => setPhotoIdx((photoIdx + 1) % gallery.length)}><ChevronRight size={40}/></button>
                        
                        <div className="lightbox-content">
                            <img src={`${API_URL}/${gallery[photoIdx]}`} alt="gallery" />
                            <p className="counter">{photoIdx + 1} / {gallery.length}</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
         </>
    );
};

export default TradePage;